/**
 * Topological Sort
 *
 * Implements Kahn's algorithm for topological sorting of service graphs.
 */

import type { ServiceGraph, GraphNode, TopologicalSortResult } from '../types/graph.js';

/**
 * Performs topological sort using Kahn's algorithm
 *
 * Returns nodes in dependency order (sources first, sinks last).
 * Detects cycles and reports which nodes are involved.
 */
export function topologicalSort(graph: ServiceGraph): TopologicalSortResult {
  if (graph.nodes.length === 0) {
    return {
      success: true,
      order: [],
      hasCycle: false,
    };
  }

  // Build adjacency list and in-degree map
  const adjacency = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  const nodeById = new Map<string, GraphNode>();

  // Initialize
  for (const node of graph.nodes) {
    adjacency.set(node.id, []);
    inDegree.set(node.id, 0);
    nodeById.set(node.id, node);
  }

  // Build adjacency list and count in-degrees
  for (const edge of graph.edges) {
    const neighbors = adjacency.get(edge.from);
    if (neighbors) {
      neighbors.push(edge.to);
    }

    const currentInDegree = inDegree.get(edge.to) ?? 0;
    inDegree.set(edge.to, currentInDegree + 1);
  }

  // Find all nodes with no incoming edges (sources)
  const queue: string[] = [];
  for (const [nodeId, degree] of inDegree) {
    if (degree === 0) {
      queue.push(nodeId);
    }
  }

  // Process nodes in topological order
  const order: GraphNode[] = [];

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const node = nodeById.get(nodeId);

    if (node) {
      order.push(node);
    }

    // Reduce in-degree of neighbors
    const neighbors = adjacency.get(nodeId) ?? [];
    for (const neighborId of neighbors) {
      const newDegree = (inDegree.get(neighborId) ?? 1) - 1;
      inDegree.set(neighborId, newDegree);

      if (newDegree === 0) {
        queue.push(neighborId);
      }
    }
  }

  // Check for cycle
  if (order.length !== graph.nodes.length) {
    // Find nodes that are part of the cycle (still have in-degree > 0)
    const cycleNodes: string[] = [];
    for (const [nodeId, degree] of inDegree) {
      if (degree > 0) {
        cycleNodes.push(nodeId);
      }
    }

    return {
      success: false,
      order: order,
      hasCycle: true,
      cycleNodes,
    };
  }

  return {
    success: true,
    order,
    hasCycle: false,
  };
}
